/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_bridge

import io.peekandpoke.klang.audio_bridge.constants.BODY_FLOOR
import io.peekandpoke.klang.audio_bridge.constants.BODY_WET
import io.peekandpoke.klang.audio_bridge.constants.COMPRESSOR_ATTACK_SECONDS
import io.peekandpoke.klang.audio_bridge.constants.COMPRESSOR_KNEE_DB
import io.peekandpoke.klang.audio_bridge.constants.COMPRESSOR_LOOKAHEAD_SECONDS
import io.peekandpoke.klang.audio_bridge.constants.COMPRESSOR_RATIO
import io.peekandpoke.klang.audio_bridge.constants.COMPRESSOR_RELEASE_SECONDS
import io.peekandpoke.klang.audio_bridge.constants.COMPRESSOR_THRESHOLD_DB
import io.peekandpoke.klang.audio_bridge.constants.DELAY_CAP
import io.peekandpoke.klang.audio_bridge.constants.DELAY_FEEDBACK
import io.peekandpoke.klang.audio_bridge.constants.DELAY_TIME_SECONDS
import io.peekandpoke.klang.audio_bridge.constants.DELAY_WET
import io.peekandpoke.klang.audio_bridge.constants.DISTORT_AMOUNT
import io.peekandpoke.klang.audio_bridge.constants.DUCK_ATTACK_SECONDS
import io.peekandpoke.klang.audio_bridge.constants.DUCK_DEPTH
import io.peekandpoke.klang.audio_bridge.constants.PHASER_CENTER_HZ
import io.peekandpoke.klang.audio_bridge.constants.PHASER_FLOOR
import io.peekandpoke.klang.audio_bridge.constants.PHASER_RATE_HZ
import io.peekandpoke.klang.audio_bridge.constants.PHASER_SWEEP_HZ
import io.peekandpoke.klang.audio_bridge.constants.PHASER_WET
import io.peekandpoke.klang.audio_bridge.constants.REVERB_SIZE
import io.peekandpoke.klang.audio_bridge.constants.REVERB_WET
import io.peekandpoke.klang.audio_bridge.constants.SLOT_UNSET
import io.peekandpoke.klang.audio_bridge.constants.VOWEL_FLOOR
import io.peekandpoke.klang.audio_bridge.constants.VOWEL_WET

/**
 * Declarative, data-driven **effect chain**: the effects one orbit (one cylinder) runs on its own
 * summed voices, before the playback's master bus, and the chain that master bus itself runs.
 *
 * A Katalyst is an ordered list of [KatalystStageDsl] stages. The list order IS the signal order,
 * so where a stage sits in the list is what it means: an `eq` before `reverb` shapes the dry mix,
 * the same `eq` after it shapes dry and tail together.
 *
 * Mirrors [IgnitorDsl] (the per-voice instrument): a `@WireFormat` root, registered by name,
 * referenced from `VoiceData.katalyst` (the orbit position) and `VoiceData.master` (the output
 * position, one per playback). One chain type, two positions, one registry (phase 3 step 12,
 * 2026-09-28): the Master DSL that the output used to run is retired. At the output nothing fills a
 * [KatalystKnobDsl.Param] slot (it is its default) and a `duck` stage is inert.
 *
 * A Katalyst rides *events*: `katalyst(...)` stamps the reference onto a pattern event, so an
 * orbit's chain can change over musical time, and `master(...)` does the same for the output.
 * `.katalyst(dsl)`
 * REPLACES the chain the pattern already carries (decided 2026-09-18): the chain is one instrument,
 * written in one place, and the way to build on the familiar one is `k.classic()` inside the
 * builder rather than a second door.
 *
 * The cylinder reads a declared chain and runs it (Katalyst step 2 onward), swapping it in with a
 * crossfade when the orbit is already sounding; an orbit that declares nothing keeps the historical
 * chain, [classic]. Every chain's SLOTS (its `Param` knobs) read the orbit's param state, the
 * born-with one included (step 5b-1), so a declaration changes which STAGES an orbit has and not
 * where a slot's value comes from; a knob declared as a fixed value stays that value.
 */
@WireFormat
data class KatalystDsl(val stages: List<KatalystStageDsl>) {
    /**
     * The knobs of [classic], one group per stage, each a [KatalystKnobDsl.Param] named `<stage>.<knob>`: the Kotlin door
     * of `Katalyst.slot.reverb.wet` (the script door hands back the SAME instances). [classic] is built from these, so a
     * handle and the chain's knob are one slot by construction, and `katp(KatalystDsl.Slots.reverb.wet, 0.3)` writes
     * exactly what `katp("reverb.wet", 0.3)` writes. The groups and their defaults: `KatalystDslSlots.kt`; why each
     * default is what it is: the KDoc of [classic].
     *
     * The 27 knobs are the whole `katp` vocabulary of the familiar chain. An authored chain names its own slots with
     * `Katalyst.param(...)`.
     */
    object Slots {
        /** The body stage: `body.material`, `body.wet`, `body.floor`. */
        val body: KatalystBodySlots = KatalystBodySlots()

        /** The vowel stage: `vowel.vowel`, `vowel.wet`, `vowel.floor`. */
        val vowel: KatalystVowelSlots = KatalystVowelSlots()

        /** The delay stage: `delay.wet`, `delay.time`, `delay.feedback`, `delay.cap`. */
        val delay: KatalystDelaySlots = KatalystDelaySlots()

        /** The reverb stage: `reverb.wet`, `reverb.size`, `reverb.lowpass`. */
        val reverb: KatalystReverbSlots = KatalystReverbSlots()

        /** The phaser stage: `phaser.rate`, `phaser.wet`, `phaser.center`, `phaser.sweep`, `phaser.floor`. */
        val phaser: KatalystPhaserSlots = KatalystPhaserSlots()

        /** The compressor stage: `compressor.threshold`, `.ratio`, `.knee`, `.attack`, `.release`. */
        val compressor: KatalystCompressorSlots = KatalystCompressorSlots()

        /** The group fader: `gain.gain`. */
        val gain: KatalystGainSlots = KatalystGainSlots()

        /** The duck stage: `duck.orbit`, `duck.depth`, `duck.attack`. */
        val duck: KatalystDuckSlots = KatalystDuckSlots()
    }

    companion object {
        /**
         * The historical chain, in the historical order: body, vowel, delay, reverb, phaser,
         * compressor, and the duck the cylinder runs after every orbit. This is what every
         * cylinder has always run, written down, plus the ONE stage that is new surface rather
         * than history: a [KatalystStageDsl.Gain] at unity, the group fader, last in the list
         * before the duck (signal-flow plan section 6, spot C, 2026-09-19). Unity is
         * bit-transparent in the stage, so the historical sound is untouched; what it buys is a
         * fader a pattern can reach with `katp("gain.gain", x)` on any orbit, declared chain or
         * not, since a gain stage is slot-driven on every chain.
         *
         * **What the fader covers.** The delay and the reverb are SEND buses whose returns are
         * mixed into the orbit's buffer by their own stages, which sit before this one, so the
         * fader scales dry and returns together, which is what a group fader should do. The duck
         * is the one thing after it: it runs outside the list, per orbit, in the cross-orbit pass
         * (see [KatalystStageDsl.Duck]), so a ducked orbit is ducked after its fader, and an orbit
         * that TRIGGERS a duck on another triggers it with its post-fader mix (lower A's fader and
         * A ducks B less hard, which is what a fader on a desk does). Both remain true when step
         * 5b makes the sends insert-style stages.
         *
         * Every knob is a **slot** ([KatalystKnobDsl.Param]) named `<stage>.<knob>`, which is the
         * vocabulary the sprudel doors write into once the cylinder reads the chain.
         *
         * **A slot's default IS its value when nobody writes it, so the classic chain has to encode
         * the engine's "off unless touched" state, slot for slot, not the shared constants.** For
         * the delay and the reverb that state is: wet, time and feedback 0.0 and cap [DELAY_CAP];
         * wet and size 0.0 (it was `VoiceFactory`'s untouched branch until the bus fields left the
         * wire in Katalyst step 5b-3).
         *
         * Note WHERE the engine's gate sits: `KatalystDelayEffect.configure` engages on
         * `time >= MIN_ACTIVE_DELAY_SECONDS` and `KatalystReverbEffect.configure` on
         * `size >= MIN_ACTIVE_SIZE`, neither of them on `wet`. So zeroing `wet` alone would NOT be
         * off, and `delay.time` / `delay.feedback` / `reverb.size` are zero here as well.
         * `delay.cap` keeps [DELAY_CAP] because the untouched voice did too: it is the soft-cap
         * shape of a line that is not running, not an amount.
         * `KatalystDefaultsSyncSpec` pins the delay, reverb, compressor and duck defaults; only the
         * phaser still has an untouched branch in `VoiceFactory`, and
         * `KatalystClassicMatchesUntouchedVoiceSpec` compares that one through the real factory.
         *
         * The families, then:
         *
         *  - zero, because the engine's untouched value is zero: `delay.wet`, `delay.time`,
         *    `delay.feedback`, `reverb.wet`, `reverb.size`, `phaser.wet`, `duck.depth`;
         *  - [SLOT_UNSET], the wire's non-finite "never set", where the off state is an absence
         *    rather than a number: all FIVE compressor slots, `duck.orbit`, `reverb.lowpass`,
         *    `body.material`, `vowel.vowel`, `body.wet` and `vowel.wet`;
         *  - the shared constant, for a knob that is inert while its gate is off and must be right
         *    the moment the gate opens: `delay.cap`, `body.floor`, `vowel.floor`, `phaser.rate`,
         *    `phaser.center`, `phaser.sweep`, `phaser.floor`, `duck.attack`;
         *  - unity, for the one knob whose untouched value is the IDENTITY of its stage rather
         *    than an off state or a tuned number: `gain.gain`. It is 1.0 written out here and in
         *    [KatalystStageDsl.Gain] rather than read from `constants/`, for the reason that KDoc
         *    gives: an identity element is not a taste decision anybody could retune.
         *
         * The compressor is the one stage whose gate is not a single knob. `KatalystCompressorWriter`
         * reads "ANY of the five set = on, and each unset one takes its constant", so four
         * constants and an unset threshold would be a compressor that is ALREADY ON with a
         * threshold to be derived. All five are unset here, so a chain that names no compressor
         * knob has none.
         *
         * **`body.wet` and `vowel.wet` follow the compressor, not the sends** (round 1 of step
         * 5a-2's review, 2026-09-18). They are [SLOT_UNSET] rather than 0.0, because 0.0 is a SET
         * value: `KatalystResonatorEffect.configure` substitutes [BODY_WET] for an unset mix exactly as
         * the compressor writer substitutes [COMPRESSOR_THRESHOLD_DB], and with a 0.0 default it never saw
         * "unset", so a material-only `body(material = "wood")` on a declared classic chain ran the bank at a
         * fully dry mix while the same call on an undeclared orbit played it at [BODY_WET]. Unset
         * is safe here and NOT on the sends, because these two stages are gated on their NAME:
         * `KatalystResonatorWriter` resolves no table for an unnamed index, so an orbit that names no material
         * cannot be switched on by a wet, however large. The engine's substitution is what makes a
         * raw `katp("body.material", 1)` behave like the door, and it is the NaN rule for a raw
         * write, not a second fill.
         *
         * **What makes a door still feel familiar is the door, not this chain**: a sprudel door
         * fills the companions of the stage a call names (`/dsl-design` §4, the rule's one home),
         * so `.reverb(wet = 0.3)` and `.body(material = "wood")` sound as they always have on this chain.
         *
         * A raw `katp(...)` is the other half of that bargain: it writes exactly ONE slot, so
         * `katp("reverb.wet", 0.3)` on this chain stays silent until `reverb.size` is written as
         * well. That is the honest consequence of slots, and it is why the doors fill.
         *
         * `body.material` and `vowel.vowel` are slots too, and they carry a NUMBER: the INDEX of a
         * name in `BodyMaterials.names` respectively `VowelBands.names` (Katalyst step 5a-2,
         * 2026-09-18). Unset here, because an untouched orbit names no material and no vowel, and
         * unset is off. That is what lets `body(material = "wood", wet = 0.3)` on a pattern keep working when
         * its orbit declares a classic chain: the door writes the index, the same way it writes the
         * wet. And `body(material = "wood")` with no wet works because the door fills the wet and the floor
         * from the same two constants the engine would have substituted.
         */
        val classic: KatalystDsl = KatalystDsl(
            listOf(
                KatalystStageDsl.Body(
                    material = Slots.body.material,
                    // Unset, not 0.0: the engine substitutes BODY_WET for an unset mix, and the
                    // stage is gated on the material, so seeding the amount switches nothing on.
                    wet = Slots.body.wet,
                    floor = Slots.body.floor,
                ),
                KatalystStageDsl.Vowel(
                    vowel = Slots.vowel.vowel,
                    // Unset for the body's reason, see the classic KDoc.
                    wet = Slots.vowel.wet,
                    floor = Slots.vowel.floor,
                ),
                KatalystStageDsl.Delay(
                    wet = Slots.delay.wet,
                    time = Slots.delay.time,
                    feedback = Slots.delay.feedback,
                    cap = Slots.delay.cap,
                ),
                KatalystStageDsl.Reverb(
                    wet = Slots.reverb.wet,
                    size = Slots.reverb.size,
                    lowpass = Slots.reverb.lowpass,
                ),
                KatalystStageDsl.Phaser(
                    rate = Slots.phaser.rate,
                    wet = Slots.phaser.wet,
                    center = Slots.phaser.center,
                    sweep = Slots.phaser.sweep,
                    floor = Slots.phaser.floor,
                ),
                // All five unset, not four constants and an unset threshold: the engine's gate is
                // "any of the five set", so four finite constants would read as a compressor that
                // is already on. See the classic KDoc.
                KatalystStageDsl.Compressor(
                    threshold = Slots.compressor.threshold,
                    ratio = Slots.compressor.ratio,
                    knee = Slots.compressor.knee,
                    attack = Slots.compressor.attack,
                    release = Slots.compressor.release,
                ),
                // The group fader, at unity: the LAST stage the orbit's mix runs through (the
                // duck below is declared last but runs outside the list, see [KatalystStageDsl.Duck]).
                // Unity is bit-transparent in `KatalystGainEffect`, which returns before it
                // multiplies, so this costs one virtual call per block and not one sample. It is
                // here so that a pattern can reach the fader with `katp("gain.gain", x)` without
                // declaring a chain at all; there is no sprudel door for it yet.
                KatalystStageDsl.Gain(
                    gain = Slots.gain.gain,
                ),
                KatalystStageDsl.Duck(
                    orbit = Slots.duck.orbit,
                    depth = Slots.duck.depth,
                    attack = Slots.duck.attack,
                ),
            )
        )

        /** Builds an orbit chain from an ordered list of stages. Kotlin, engine-level; the script door is `Katalyst(k => ...)`. */
        fun of(vararg stages: KatalystStageDsl): KatalystDsl = KatalystDsl(stages.toList())
    }
}

/**
 * One stage in a [KatalystDsl] chain, applied to the orbit in list order.
 *
 * Stages are thin declarations: the paragraphs below are the CONTRACT a stage carries, and
 * `KatalystChainBuilder` is the one place that turns each one into the effect the engine runs.
 *
 * **The contract: a stage is a shell over the shared DSP classes in `audio_be/`** (the same
 * `Compressor` / `Reverb` / `DelayLine` every host uses). No stage may introduce its own DSP
 * implementation, which is what keeps one effect one sound on every bus.
 *
 * **Every knob is a [KatalystKnobDsl]**, the Katalyst's own knob language and not an [IgnitorDsl] node
 * (KLANG-00302 (katalyst knob expressions), D1): a [KatalystKnobDsl.Constant], a
 * [KatalystKnobDsl.Param], or an expression over them (step 4, D4), read once per block. An Ignitor node on a knob is
 * a type error at the door (the script door's stage call; the Kotlin door's constructors do not take one), never
 * coerced. The Motor stays raw, so a knob is never clamped beyond what
 * the underlying DSP already does. **The exceptions are the knobs fixed per chain**, plain numbers because they size
 * or pick something when the chain is built, so a slot there could never move, and the type says so:
 * [Compressor.lookahead] (a delay ring, phase 3 step 12 C2) and [Distort.shape]. The other is
 * [Eq]: its sections stay the [IgnitorDsl.EqSection] the voice uses (decision Q2, E2).
 *
 * **Every knob keeps the name and the scale of its sprudel door**, so a number means the same
 * thing whether it is written on a pattern or in a chain. A BARE stage means "I reached for this
 * effect", so its knob defaults are the shared touched constants from
 * `constants/BusEffectDefaults.kt`, never a literal here.
 *
 * Four stages are deliberately NOT audible bare, matching their sprudel doors. Three of them wait
 * for a NAME: [Body] and [Vowel] until `material` respectively `vowel` names an index, and [Duck]
 * until `orbit` names a source; all three carry [SLOT_UNSET] on that one knob. The duck waits for
 * a DEPTH as well, because its touched [DUCK_DEPTH] is 0, so naming the source alone is still
 * silent. [Phaser] is the fourth without a name knob at all: its `wet` carries its own touched
 * [PHASER_WET], which is 0.0 and therefore below the engine's `Phaser.MIN_ACTIVE_DEPTH` of 0.01.
 * Every other stage carries its touched constants throughout and a bare one is a working effect.
 *
 * [KatalystDsl.classic] is the other case entirely and reads differently: nobody reached for
 * anything, so every slot carries the engine's untouched value.
 *
 * All values are tune-by-ear starting points; the engine is intentionally raw, so nothing here is
 * clamped for "safety" beyond what the underlying DSP already does.
 */
@WireFormat
sealed interface KatalystStageDsl {

    /**
     * Resonant body: a bank of narrow modes over a broadband floor, the box a sound sits in.
     *
     * The material is a knob like every other one, and the number it carries is an INDEX into
     * `BodyMaterials.names` (Katalyst step 5a-2, 2026-09-18). That is what keeps a name off the
     * wire as a string and lets a pattern's `body(material = "wood")` reach a declared chain through
     * `katp("body.material", ...)`: both doors convert through `BodyMaterials.indexOf`, one place.
     * Index 0 is `none`, and so is an unset, negative or out-of-range index: the stage is off.
     *
     * @param material the material's index in `BodyMaterials.names`, the number
     *   `BodyMaterials.indexOf("wood")` gives. The default is [SLOT_UNSET]: the chain names no
     *   material and the stage is off. Write a name on either builder door
     *   (`k.body(material = "wood")`) and it is converted for you. Orbit twin: `body(material = "wood")`.
     * @param wet how much of the orbit runs through the body, 0 to 1. Orbit twin: `body(wet = ...)`.
     * @param floor minimum dry share kept in the mix, 0 to 1. Lower = the modes sit over less dry
     *   and the body is more audible. Orbit twin: `body(floor = ...)`.
     */
    @WireName("body")
    data class Body(
        val material: KatalystKnobDsl = KatalystKnobDsl.Constant(SLOT_UNSET),
        val wet: KatalystKnobDsl = KatalystKnobDsl.Constant(BODY_WET),
        val floor: KatalystKnobDsl = KatalystKnobDsl.Constant(BODY_FLOOR),
    ) : KatalystStageDsl

    /**
     * Formant bank: the vowel a sound sings, as a set of resonances over a low broadband floor.
     *
     * The vowel is an INDEX into `VowelBands.names`, the flat `"<register>:<vowel>"` catalogue, for
     * the same reason the body's material is (see [Body]). Index 0 is `none`, and so is an unset or
     * out-of-range index: the stage is off.
     *
     * @param vowel the vowel's index in `VowelBands.names`, the number `VowelBands.indexOf("a")`
     *   gives (a bare name is the soprano register). The default is [SLOT_UNSET]: the chain names
     *   no vowel and the stage is off. Write a name on either builder door
     *   (`k.vowel(vowel = "bass:a")`) and it is converted for you. Orbit twin: `vowel(vowel = "a")`.
     * @param wet how much of the orbit runs through the formant bank, 0 to 1. Orbit twin:
     *   `vowel(wet = ...)`.
     * @param floor minimum dry share kept between the formants, 0 to 1. Much lower than the body's:
     *   a vowel is a source strongly shaped by its formants. Orbit twin: `vowel(floor = ...)`.
     */
    @WireName("vowel")
    data class Vowel(
        val vowel: KatalystKnobDsl = KatalystKnobDsl.Constant(SLOT_UNSET),
        val wet: KatalystKnobDsl = KatalystKnobDsl.Constant(VOWEL_WET),
        val floor: KatalystKnobDsl = KatalystKnobDsl.Constant(VOWEL_FLOOR),
    ) : KatalystStageDsl

    /**
     * Orbit delay, the shared `DelayLine` (audio_be). Same names, scales and defaults as the
     * sprudel `delay(...)` door; the same stage runs at the output (`master(...)`).
     *
     * @param wet how much of the orbit goes into the delay (0.0 = off). Orbit twin: `delay(wet = x)`.
     * @param time delay time in seconds. Orbit twin: `delay(time = ...)`.
     * @param feedback feedback amount; allowed above 1.0 (raw engine). At exactly 1.0 the echoes
     *   neither fade nor grow: each keeps the level it went in at (a delay time that falls between
     *   two samples dulls its highs a little on every repeat), unless that level reaches [cap].
     *   Above 1.0 they grow on every repeat until [cap] holds them, just under it.
     *   Orbit twin: `delay(feedback = ...)`.
     * @param cap level the recirculating signal saturates toward: how loud the echoes settle once
     *   [feedback] is above 1.0. Up to 95% of it the echoes pass untouched, so at a feedback of
     *   1.0 or less it acts only on echoes loud enough to reach it. Orbit twin: `delay(cap = ...)`.
     */
    @WireName("delay")
    data class Delay(
        val wet: KatalystKnobDsl = KatalystKnobDsl.Constant(DELAY_WET),
        val time: KatalystKnobDsl = KatalystKnobDsl.Constant(DELAY_TIME_SECONDS),
        val feedback: KatalystKnobDsl = KatalystKnobDsl.Constant(DELAY_FEEDBACK),
        val cap: KatalystKnobDsl = KatalystKnobDsl.Constant(DELAY_CAP),
    ) : KatalystStageDsl

    /**
     * Orbit reverb, the shared Freeverb `Reverb` (audio_be). Same names, scales and defaults as the
     * sprudel `reverb(...)` door; the same stage runs at the output (`master(...)`).
     *
     * @param wet how much of the orbit goes into the reverb (0.0 = off). Orbit twin: `reverb(wet = x)`.
     * @param size tail length on the authored 0 to 10 scale (the backend divides by 10, see
     *   `Reverb.normalizeSize`). 3 is about a 1 s tail, 5 about 1.4 s, 10 about 12.5 s; the
     *   shortest reachable is about 0.7 s, and above 10 is bounded at 10. Orbit twin:
     *   `reverb(size = ...)`.
     * @param lowpass high-frequency damping of the tail as a cutoff in Hz; lower is darker. Null,
     *   or a non-finite value such as [SLOT_UNSET], = the engine's fixed default damping. That
     *   default is a sample-rate relation rather than a number, which is why this knob alone has no
     *   wire constant: [KatalystDsl.classic] carries it as a slot whose default is "unset".
     *   Orbit twin: `reverb(lowpass = ...)`.
     */
    @WireName("reverb")
    data class Reverb(
        val wet: KatalystKnobDsl = KatalystKnobDsl.Constant(REVERB_WET),
        val size: KatalystKnobDsl = KatalystKnobDsl.Constant(REVERB_SIZE),
        val lowpass: KatalystKnobDsl? = null,
    ) : KatalystStageDsl

    /**
     * Orbit phaser: a sweeping all-pass notch comb, the drifting one the chain owns (a mic's comb
     * deliberately stands still).
     *
     * @param rate sweep rate in Hz. 0 = the sweep stands still. Orbit twin: `phaser(rate = ...)`.
     * @param wet wet amount, 0 to 1 (the wire spelling on the voice is `phaserDepth`). Orbit twin:
     *   `phaser(wet = ...)`.
     * @param center center frequency of the sweep in Hz. Orbit twin: `phaser(center = ...)`.
     * @param sweep width of the sweep around [center], in Hz. Orbit twin: `phaser(sweep = ...)`.
     * @param floor minimum dry coefficient of the wet/dry law; 1.0 = purely additive. Orbit twin:
     *   `phaser(floor = ...)`.
     */
    @WireName("phaser")
    data class Phaser(
        val rate: KatalystKnobDsl = KatalystKnobDsl.Constant(PHASER_RATE_HZ),
        val wet: KatalystKnobDsl = KatalystKnobDsl.Constant(PHASER_WET),
        val center: KatalystKnobDsl = KatalystKnobDsl.Constant(PHASER_CENTER_HZ),
        val sweep: KatalystKnobDsl = KatalystKnobDsl.Constant(PHASER_SWEEP_HZ),
        val floor: KatalystKnobDsl = KatalystKnobDsl.Constant(PHASER_FLOOR),
    ) : KatalystStageDsl

    /**
     * Orbit compressor: the group dynamics, after the EQ so the detector sees the corrected
     * spectrum and a low cut turns into headroom.
     *
     * The knobs follow sprudel (`threshold`, `knee`, `attack`).
     *
     * A LIMITER is this stage with limiter numbers, not a stage of its own: the `limiter(...)` door on
     * the Katalyst builder appends one (phase 3 step 12 C2), so one DSP has one wire word.
     *
     * @param threshold ceiling in dBFS where gain reduction starts. Orbit twin:
     *   `compressor(threshold = ...)`.
     * @param ratio compression ratio above the threshold (4.0 = 4:1). Orbit twin:
     *   `compressor(ratio = ...)`.
     * @param knee soft-knee width in dB; a hard corner injects harmonics on every crossing. Orbit
     *   twin: `compressor(knee = ...)`.
     * @param attack how fast the gain closes, in seconds. Orbit twin: `compressor(attack = ...)`.
     * @param release how fast the gain opens again, in seconds. Orbit twin:
     *   `compressor(release = ...)`.
     * @param lookahead how far ahead the compressor sees, in seconds, and by how much it delays
     *   whatever runs through it. 0 (the default) is no lookahead and no latency. From 8 samples on (about 0.2 ms)
     *   the gain starts closing BEFORE a transient arrives, and [attack] becomes the gain-smoothing
     *   length rather than a one-pole time (widen both together). The cost is latency: on an orbit
     *   THAT orbit runs late by it, and nothing compensates. A plain number fixed when the chain is
     *   built, never a slot (it sizes a delay ring); bounded to 0.05 s, and a negative or non-finite
     *   value is 0. No orbit twin: a sprudel door writes slots on the running chain, and a slot
     *   cannot size a ring, so declare it with `katalyst(Katalyst(k => k.limiter(lookahead = ...)))`.
     */
    @WireName("compressor")
    data class Compressor(
        val threshold: KatalystKnobDsl = KatalystKnobDsl.Constant(COMPRESSOR_THRESHOLD_DB),
        val ratio: KatalystKnobDsl = KatalystKnobDsl.Constant(COMPRESSOR_RATIO),
        val knee: KatalystKnobDsl = KatalystKnobDsl.Constant(COMPRESSOR_KNEE_DB),
        val attack: KatalystKnobDsl = KatalystKnobDsl.Constant(COMPRESSOR_ATTACK_SECONDS),
        val release: KatalystKnobDsl = KatalystKnobDsl.Constant(COMPRESSOR_RELEASE_SECONDS),
        val lookahead: Double = COMPRESSOR_LOOKAHEAD_SECONDS,
    ) : KatalystStageDsl

    /**
     * Distortion of the bus mix: the Ignitor's `distort` at the bus position, one DSP and one word on
     * both hosts (KLANG-00292 (katalyst distort stage)). A clipper is this stage with a clipping
     * shape (`soft`, `hard`) and a small [amount]; saturation is a gentle shape at a moderate one.
     *
     * Unlike a voice's distort it bends the SUM: the notes of the bus meet inside one curve, so they
     * intermodulate. That is the glue a master saturator is for and the growl of a guitar amp fed by
     * the whole chord; pushed hard, chords turn to mush. Per-note distortion stays the voice's.
     *
     * The law is the voice's fused distort (`DistortionCore`): `shape(x * 10^(amount * 1.2))`, then a DC
     * blocker, no soft cap, run on each channel on its own. The house limiter after the master is the safety
     * net; nothing here is clamped. It runs at the bus's rate: to distort with less aliasing, write it inside a
     * region (`oversampleAtLeast(2) { it.distort(...) }`). Its own oversampling factor (2x by default) retired
     * with KLANG-00214 (oversampling regions) step 4.
     *
     * @param amount the drive, `10^(amount * 1.2)` into the shape: 0.1 is about +2.4 dB, 0.25 about
     *   +6 dB, 0.5 about +12 dB. Default [DISTORT_AMOUNT]. At or below 0, or unset, the stage is off
     *   and the bus passes undistorted. A slot (`Katalyst.param`) moves it with a glide. No orbit twin:
     *   sprudel's `distort(...)` is the voice's door.
     * @param shape the waveshaper as an INDEX into [DistortionShapes.names]; the doors take a name.
     *   A plain number fixed with the chain, never a slot: it picks the shaper when the chain is
     *   built, as a voice reads it once per note. An unknown or out-of-range index is `soft`.
     */
    @WireName("distort")
    data class Distort(
        val amount: KatalystKnobDsl = KatalystKnobDsl.Constant(DISTORT_AMOUNT),
        val shape: Int = DistortionShapes.SOFT_INDEX,
    ) : KatalystStageDsl

    /**
     * Sidechain duck: this orbit is pulled down whenever the orbit it listens to sounds.
     *
     * **Declared in the list, but run outside it**, as it is today: ducking needs every orbit
     * processed first, so `Cylinders.processAndMix` runs it after the chains. Its position in the
     * list is therefore ignored, deliberately.
     *
     * When a chain declares more than one duck stage the LAST one wins, because the cylinder runs
     * exactly one ducking effect; the earlier ones are silently ignored rather than summed
     * (decided with the maintainer, 2026-09-17, recorded in KLANG-00236 (katalyst dsl)).
     *
     * @param orbit the orbit to listen to, as a number the runtime coerces to an Int. The default
     *   is [SLOT_UNSET]: no source named, no ducking. A consumer tests `isFinite()` and never
     *   compares, so a finite negative is an orbit REQUEST like any other, not an off switch.
     *   Orbit twin: `duck(orbit = ...)`.
     * @param depth how far this orbit is pulled down, 0 to 1. 0 = no ducking. Orbit twin:
     *   `duck(depth = ...)`.
     * @param attack how fast the duck closes, in seconds. Orbit twin: `duck(attack = ...)`.
     */
    @WireName("duck")
    data class Duck(
        val orbit: KatalystKnobDsl = KatalystKnobDsl.Constant(SLOT_UNSET),
        val depth: KatalystKnobDsl = KatalystKnobDsl.Constant(DUCK_DEPTH),
        val attack: KatalystKnobDsl = KatalystKnobDsl.Constant(DUCK_ATTACK_SECONDS),
    ) : KatalystStageDsl

    /**
     * The mix equalizer: one stage whose inside is a list of sections, applied left to right.
     *
     * Reuses [IgnitorDsl.EqSection] verbatim, so a section means the same thing on a voice and on
     * an orbit. This is the one place a Katalyst stage still carries Ignitor nodes (decision Q2 of
     * KLANG-00302 (katalyst knob expressions), E2: the sections are a DSP law shared with the voice and
     * with
     * `bands`). The Katalyst door lets a section knob be a number or a `Kat.param` only, as a
     * [IgnitorDsl.Constant] or a [IgnitorDsl.Param]; the engine reads any other node there as unset.
     *
     * There is no default position: a stage sits where it is written, and nothing reorders it. An EQ
     * after the reverb and before the dynamics shapes the room with the dry and lets the detector
     * see the result; write it there if you want that. Note that
     * `k.classic().eq(...)` does NOT land there: it appends, so the EQ ends up after the compressor
     * AND after classic's own group fader (the duck runs outside the list regardless, see [Duck]).
     * Write the stages out in order when the position matters.
     *
     * **What the position means.** The delay and the reverb are fed from the orbit mix at their own
     * position in the list (Katalyst step 5b-2), so an `eq` written BEFORE `reverb` shapes the dry
     * signal AND what the room hears: the room hears the cab. The same `eq` written AFTER it shapes
     * dry and tail together, after the room. Both are legitimate mixes and the list order is how
     * they are told apart.
     *
     * @param sections the sections in written order; an empty list is a transparent stage.
     */
    @WireName("eq")
    data class Eq(
        val sections: List<IgnitorDsl.EqSection> = emptyList(),
    ) : KatalystStageDsl

    /**
     * Make-up gain on the orbit: the group fader, after the inserts.
     *
     * The structural fix for mixing an orbit deliberately low (to keep its compressor out of plop
     * territory) and bringing the level back up at the end of its chain.
     *
     * **What a fader covers is list order**: the delay and the reverb are fed from the orbit's mix
     * at their own positions and add their RETURNS into it there (Katalyst step 5b-2), so a gain
     * stage written after them scales dry and returns alike, and one written BEFORE them scales the
     * dry and what they are fed, and so their returns too. The duck is
     * the one stage no list position can put before this one: it runs outside the list, in the
     * cross-orbit pass ([Duck]), so it always lands after the fader.
     *
     * That ordering has one consequence worth naming, and it is the RIGHT behaviour rather than a
     * corner: a duck on orbit B reads orbit A's mix as its trigger, and by then A's fader has
     * already run, so lowering A's fader also lowers how hard A ducks B. A group fader moves the
     * whole group, sends and sidechain sends included, which is what a fader on a desk does.
     *
     * [KatalystDsl.classic] carries one of these at unity, as its last stage before the duck, so
     * `katp("gain.gain", x)` reaches the group fader on any orbit (signal-flow plan section 6,
     * spot C). There is no sprudel door for it yet; a door is one line if the songs want it.
     *
     * **A chain may declare more than one, and they MULTIPLY**, in list order, like any two
     * stages: `k.classic().gain(0.8)` is classic's unity slot then 0.8. Two stages whose knobs are
     * `Param`s of the SAME name read the same key, so one `katp` write applies once per stage
     * (0.5 on two such stages is 0.25). Deliberate, and the reason a second fader that should move
     * on its own needs a slot name of its own.
     *
     * @param gain linear gain factor (1.0 = unity, 2.0 is about +6 dB). Unity is the identity
     *   element of the stage, not a tuned value, which is why it is written here rather than read
     *   from `constants/`.
     */
    @WireName("gain")
    data class Gain(
        val gain: KatalystKnobDsl = KatalystKnobDsl.Constant(1.0),
    ) : KatalystStageDsl

    // ── Arithmetic: the Ignitor's operators on the bus signal ───────────────────────────────────

    /*
     * The arithmetic stages (KLANG-00300 (katalyst arithmetic)):
     * each applies its Ignitor twin's per-sample law
     * (`_arithmetic_laws.kt` in `audio_be`, one function per operator) to every sample of both channels, so a bus
     * sample and a voice sample that meet the same numbers come out the same, clamps included (bit for bit against
     * the authored node; an optimized voice divides by multiplying with the reciprocal, one rounding apart). The
     * operand is a knob like every other: a `Constant` or a `Param`, read once per block. Two differences from the
     * Ignitor, both because this is an orbit knob and not a voice's: a moving operand GLIDES per sample over
     * `KNOB_GLIDE_SECONDS` (a divisor as its reciprocal, so a move never passes a huge gain), and a non-finite one
     * reads as unset (the operator's identity; for `Min` and `Max` the stage passes the bus).
     * Memoryless: no latency, no tail.
     */

    /** Adds [other] to every sample (the Ignitor's `Plus`, bare: no clamp). A constant offset is DC on the bus. */
    @WireName("plus")
    data class Plus(val other: KatalystKnobDsl) : KatalystStageDsl

    /** Subtracts [other] from every sample (the Ignitor's `Minus`, bare: no clamp). */
    @WireName("minus")
    data class Minus(val other: KatalystKnobDsl) : KatalystStageDsl

    /**
     * Multiplies every sample by [other] (the Ignitor's `Times`, the output clamped by `safeOut`). Not the fader:
     * [Gain] is the tone-neutral level, which does not clamp, skips unity and is the `gain.gain` slot of the classic
     * chain; this is the arithmetic, identical to the voice's. A polarity flip is `Times(-1)`.
     */
    @WireName("times")
    data class Times(val other: KatalystKnobDsl) : KatalystStageDsl

    /** Divides every sample by [other] (the Ignitor's `Div`: a divisor of exactly zero gives zero, the output clamped). */
    @WireName("div")
    data class Div(val other: KatalystKnobDsl) : KatalystStageDsl

    /** Raises every sample to [exp] (the Ignitor's `Pow`, signed magnitude: a negative sample keeps its sign). */
    @WireName("pow")
    data class Pow(val exp: KatalystKnobDsl) : KatalystStageDsl

    /**
     * The per-sample MINIMUM of the bus and [other]: a cap. Mathematical, like the Ignitor's `Min` node; the doors are
     * clamps, so the `max` door ("at most") builds this one. Do not "correct" the crossing.
     */
    @WireName("min")
    data class Min(val other: KatalystKnobDsl) : KatalystStageDsl

    /**
     * The per-sample MAXIMUM of the bus and [other]: a floor. Mathematical, like the Ignitor's `Max` node; the `min`
     * door ("at least") builds this one.
     */
    @WireName("max")
    data class Max(val other: KatalystKnobDsl) : KatalystStageDsl

    /** The absolute value of every sample: full-wave rectification (the Ignitor's `Abs`). No knob. */
    @WireName("abs")
    data object Abs : KatalystStageDsl

    /**
     * Branches side by side: every branch runs on its own copy of the bus at this position, and the
     * stage's output is the SUM of the branches (KLANG-00216 (signal graph engine) §6.9).
     * A branch is a chain of its own, so `parallel` is the twin of a chain's series. An empty branch
     * is the dry signal: `parallel(distorted, empty)` is parallel distortion. The doors write a
     * stage only for two branches or more (none is the bus unchanged, one is that branch's stages
     * in place).
     *
     * **Matched in phase:** a branch that delays the bus (a compressor's lookahead, an oversampling
     * region) would comb against the others in the sum, so every branch gets an unshaped round trip
     * of each oversampler it lacks (a phase twin) and a delay for the lookahead it lacks. The stage
     * delays the bus by all the branches' oversamplers and the longest lookahead.
     *
     * **The sum is plain:** two identical branches are twice the level (+6 dB), as two faders up on
     * a desk; a branch scales itself (`gain`) where the author wants a blend. A `delay` or `reverb`
     * adds its return on top of the dry at its position, so a branch holding one carries the dry too:
     * `parallel(empty, reverb)` is the dry twice plus the room. Parallel distortion and compression,
     * which change the signal itself, do not have this.
     *
     * **A `duck` in a branch is the orbit's duck**: the duck runs outside the list after every orbit
     * and its position is ignored ([Duck]), so a branch is no different; the last declared duck,
     * branches included in the order written, wins.
     *
     * @param branches the chains that run side by side, each from the bus at this position.
     */
    @WireName("parallel")
    data class Parallel(
        val branches: List<KatalystDsl>,
    ) : KatalystStageDsl

    /**
     * An oversampling REGION of the bus that raises the rate by [factor]: the stages of [body] run at the incoming rate
     * times [factor] (1x to 2x, 4x to 8x), and the region hands back the rate it was entered with. The bus at this
     * position is converted up once where it enters and down once at the end (KLANG-00214 (oversampling regions)).
     * At the head of a chain it makes the orbit run at that rate: one conversion per orbit, not per voice.
     *
     * [factor] is a plain number fixed with the chain, as the `distort` stage's `shape`: rounded down to a power
     * of two (3 is 2, 5 and 7 are 4); 1 or less is no region, the body's stages in place. The rate stops at 16x: a
     * guard against absurd values, not an error. Inside, every stage keeps its sound in seconds and Hz (a delay its
     * time, a reverb its room, a filter its cutoff). A `duck` in the body is
     * the orbit's duck, as in a `parallel` branch. Twin: [OversampleAtLeast], the floor.
     */
    @WireName("oversampleBy")
    data class OversampleBy(
        val factor: Int,
        val body: KatalystDsl,
    ) : KatalystStageDsl

    /**
     * An oversampling REGION of the bus with a floor: the stages of [body] run at [factor] times the base rate AT
     * LEAST, stepping up only the 2x steps that are missing; at or above the floor it is no region and converts
     * nothing. The door for reusable pieces: a preset that needs 4x adds nothing inside an 8x region. [factor] and
     * [body] as in [OversampleBy], the twin.
     */
    @WireName("oversampleAtLeast")
    data class OversampleAtLeast(
        val factor: Int,
        val body: KatalystDsl,
    ) : KatalystStageDsl
}
